import React, {FC, useEffect, useMemo, useState} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  IconBtn,
  ModuleTitleText,
  UiInput,
  UiDatePicker,
} from '@ru-digital-assets/ui-component';
import UiSelect, { OptionType } from '../../../modules/ui/ui-select/ui-select';
import { useFormik } from 'formik';

import DetailsContainer from '../../../containers/details-container';
import FormFooter from '../../../containers/form-footer';
import AppPageMain from '../../../modules/app-page/app-page--main';
import PageHeader from '../../../modules/page-header';
import { useAppDispatch } from '../../../store';
import { initPurchase } from '../../../store/actions/operations-sign';
import { RootState } from '../../../types';
import currencyFormat from '../../../utils/currencyFormat';

import BuyIcon from './icons/buy-icon.svg';

import './index.scss';
import { getPaymentTokens } from "../../../store/actions/common-info";
type FormStateType = {
  accountId?: number;
  amount?: number;
  companyId?: number;
  tokenCount?: number;
  tokenName?: string;
  personal?: boolean;
  expiryDate?: string;
};

const initialFormState: FormStateType = {};

const AssetsBuyingPage: FC = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();

  const [loadingBtn, setLoadingBtn] = useState(false);
  const [tokenCountLimit, setTokenCountLimit] = useState(-1);
  const [validTokenCount, setValidTokenCount] = useState(true);

  const paymentTypes = useSelector(
    (state: RootState) => state.commonInfo.paymentTokens
  );
  const accountId = useSelector((state: RootState) => state.auth.account.id);
  const companyInfos = useSelector(
    (state: RootState) => state.companiesInfo.companies
  );

  const paymentOptionTypes = useMemo(
    () =>
      paymentTypes.map((item) => {
        const opt: OptionType = {
          value: item.code,
          label: item.name,
          limit: item.totalCount,
        };
        return opt;
      }),
    [paymentTypes]
  );

  useEffect(() => {
    dispatch(getPaymentTokens());
  }, []);

  const companies: OptionType[] = useMemo(() => {
    const arr = [];
    for (let key in companyInfos) {
      if (accountId.toString() !== key) {
        arr.push({
          value: key,
          label: `${companyInfos[key].shortName} (ИНН ${companyInfos[key].tin})`,
        });
      }
    }
    return arr;
  }, [companyInfos]);

  const formState = useFormik({
    initialValues: initialFormState,
    onSubmit: (values) => {
      setLoadingBtn(true);
      dispatch(
        initPurchase({
          info: {
            ...values,
            accountId,
          },
          callback: (data) => {
            setLoadingBtn(false);
            history.push('/assets/signing', { id: data.id });
          },
        })
      ).catch(() => {
        setLoadingBtn(false);
      });
    },
  });

  return (
    <AppPageMain>
      <div className="container">
        <div className="row">
          <div className="col-19 col-lg-27 col-xl-35">
            <PageHeader
              items={[
                { link: '/assets', title: 'Активы' },
                { link: '#', title: 'Покупка' },
              ]}
              iconType="assets"
            />
          </div>
        </div>
        <div className="buying-container">
          <DetailsContainer
            title="Покупка цифровых финансовых активов"
            onClose={() => history.back()}
            infoTitle={{
              steps: [
                { title: 'Параметры', active: true },
                { title: 'Подпись' },
                { title: 'Согласование' },
                { title: 'Подтвеждение оплаты' },
              ],
            }}
            leftComponent={
              <form className="form-assets">
                <div className="form-assets__item">
                  <p className="p p_font-size_14 p_color_grey mb_8">
                    Выберите желаемый токен для покупки
                  </p>
                  <div className="form-item">
                    <UiSelect
                      placeholder="Выберите токен из списка"
                      options={paymentOptionTypes}
                      lg
                      onChange={(val) => {
                        let option = val as OptionType;
                        formState.setFieldValue('tokenName', option?.value);
                        setTokenCountLimit(
                          option?.limit !== undefined ? option.limit : -1
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="form-assets__item flex form-assets__token-count">
                  <UiInput
                    className="input-left"
                    required
                    rightText="шт"
                    name="tokenCount"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      if (e.target.value) {
                        let count = Number(e.target.value.replace(/\s/g, ''));
                        formState.setFieldValue('tokenCount', count);
                        if (count !== undefined) {
                          if (tokenCountLimit >= count) {
                            setValidTokenCount(true);
                          } else {
                            setValidTokenCount(false);
                          }
                        }
                      } else {
                        formState.setFieldValue('tokenCount', null);
                      }
                    }}
                    error={!validTokenCount}
                    lg
                    label={'Количеств токенов'}
                  />
                  <div
                    className="flex justify-center align-center"
                    style={{ width: 'auto' }}
                  >
                    X
                  </div>
                  <UiInput
                    className="input-right"
                    required
                    rightText="₽"
                    name="amount"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      formState.setFieldValue(
                        'amount',
                        e.target.value
                          ? Number(e.target.value.replace(/\s/g, ''))
                          : null
                      );
                    }}
                    lg
                    label="Цена покупки одного токена"
                  />
                </div>
                {!validTokenCount && (
                  <div className="form-item__footer">
                    <span className="red">
                      Значение больше, чем общее число токенов в эмиссии
                    </span>
                    <br />
                    <span className="red">
                      Общая сумма эмиссии: {tokenCountLimit} токенов
                    </span>
                    <br />
                    <br />
                  </div>
                )}
                <div className="form-assets__item">
                  <p className="p p_font-size_14 p_color_grey mb_8">
                    Укажите Продавца для осуществления сделки
                  </p>
                  <div className="form-item">
                    <UiSelect
                      placeholder="Продавец"
                      options={companies}
                      onChange={(val) => {
                        formState.setFieldValue(
                          'companyId',
                          Number((val as OptionType)?.value)
                        );
                        formState.setFieldValue('personal', true);
                      }}
                    />
                  </div>
                </div>
                <p className="p p_font-size_14 p_color_grey mb_8">
                  Срок для оплаты токенов
                </p>
                <div className="form-assets__expire-date">
                  <div>
                    <UiDatePicker
                      selected={formState.values.expiryDate}
                      minDate={new Date()}
                      dateFormat="dd.MM.yyyy"
                      onChange={(value: string) =>
                        formState.setFieldValue('expiryDate', value)
                      }
                    />
                  </div>
                  <p className="p p_font-size_12 p_color_grey">
                    Срок, в течение которого заявка должна быть акцептована
                    Продавцом, а затем оплачена Покупателем. Если токены не
                    будут оплачены в течение этого срока, сделка будет отменена.
                  </p>
                </div>
                <FormFooter skipCheck>
                  <div className="flex justify-end">
                    <div className="form-assets__flex">
                      <div className="form-assets__item form-assets__item--sm">
                        <ModuleTitleText
                          top={
                            formState.values &&
                            formState.values.tokenCount &&
                            formState.values.amount
                              ? 'Вы покупаете цифровые финансовые активы на сумму'
                              : ''
                          }
                          bottom={
                            formState.values &&
                            formState.values.tokenCount &&
                            formState.values.amount
                              ? currencyFormat(
                                  formState.values.amount *
                                    formState.values.tokenCount
                                )
                              : ''
                          }
                        />
                      </div>
                    </div>
                    <IconBtn
                      disabled={
                        loadingBtn ||
                        !validTokenCount ||
                        !formState.values.amount ||
                        !formState.values.companyId ||
                        !formState.values.tokenCount ||
                        !formState.values.tokenName ||
                        !formState.isValid ||
                        !formState.values.expiryDate
                      }
                      onClick={formState.handleSubmit}
                      icon={BuyIcon}
                      title="Сформировать заявку о покупке ЦФА"
                    />
                  </div>
                </FormFooter>
              </form>
            }
          />
        </div>
      </div>
    </AppPageMain>
  );
};

export default AssetsBuyingPage;
